<template>
  <div>
    <SiteHeader :nav-items="navItems" :show-search="false" />
    <FloatingRightMenu :items="floatingMenuItems" />

    <main class="bg-stone-50 pb-20 md:pb-10">
      <section class="bg-[radial-gradient(circle_at_top_left,_rgba(255,126,0,0.16),_transparent_34%),linear-gradient(180deg,#fff8f1_0%,#fafaf9_100%)]">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 py-10 md:grid-cols-[1.02fr_0.98fr] md:py-16">
          <div v-scroll-reveal="'fade-right'" class="flex flex-col justify-center">
            <div class="inline-flex w-fit rounded-full border border-brand-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-brand-700">
              {{ content.hero.badge }}
            </div>
            <h1 class="mt-5 max-w-4xl text-4xl font-semibold leading-tight text-stone-900 md:text-6xl">
              {{ content.hero.heading }}
            </h1>
            <p class="mt-5 max-w-3xl text-lg leading-8 text-stone-700">{{ content.hero.subheading }}</p>
            <p class="mt-5 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.hero.description }}</p>

            <div class="mt-8 flex flex-wrap gap-3">
              <a href="#services" class="rounded-full bg-brand-500 px-6 py-3.5 font-semibold text-white shadow-float transition hover:bg-brand-600">
                {{ content.hero.ctaPrimary }}
              </a>
              <a href="#contact" class="rounded-full border border-stone-300 bg-white px-6 py-3.5 font-semibold text-stone-700 shadow-soft transition hover:border-brand-300 hover:text-brand-600">
                {{ content.hero.ctaSecondary }}
              </a>
            </div>

            <div v-scroll-reveal-stagger class="mt-8 grid gap-3 sm:grid-cols-3">
              <div v-for="stat in content.hero.stats" :key="stat.label" class="rounded-[1.6rem] border border-stone-200 bg-white p-5 shadow-soft">
                <div class="text-2xl font-semibold text-stone-900 md:text-3xl">{{ stat.value }}</div>
                <div class="mt-2 text-sm font-medium text-stone-500">{{ stat.label }}</div>
              </div>
            </div>
          </div>

          <div v-scroll-reveal="{ variant: 'fade-left', delay: 120 }">
            <div class="overflow-hidden rounded-[2rem] border border-stone-200 bg-white p-3 shadow-soft">
              <img :src="currentSlide.image" :alt="currentSlide.title" class="h-[260px] w-full rounded-[1.5rem] object-cover md:h-[430px]" />
              <div class="px-3 pb-2 pt-4 md:px-4">
                <div class="inline-flex rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
                  {{ currentSlide.badge }}
                </div>
                <div class="mt-3 text-2xl font-semibold text-stone-900">{{ currentSlide.title }}</div>
                <p class="mt-2 text-sm leading-7 text-stone-600 md:text-base">{{ currentSlide.description }}</p>
              </div>
            </div>

            <div class="mt-4 flex items-center justify-between gap-3">
              <div class="flex gap-2">
                <button
                  v-for="(slide, index) in content.hero.slides"
                  :key="slide.title"
                  type="button"
                  class="h-2.5 rounded-full transition"
                  :class="currentSlideIndex === index ? 'w-8 bg-brand-500' : 'w-2.5 bg-stone-300'"
                  @click="currentSlideIndex = index"
                />
              </div>
              <div class="flex gap-2">
                <button type="button" class="grid h-11 w-11 place-items-center rounded-full border border-stone-200 bg-white text-stone-600 shadow-soft" @click="prevSlide">
                  <AppIcon name="chevron-left" class="h-5 w-5" />
                </button>
                <button type="button" class="grid h-11 w-11 place-items-center rounded-full border border-stone-200 bg-white text-stone-600 shadow-soft" @click="nextSlide">
                  <AppIcon name="chevron-right" class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="about" class="mx-auto grid max-w-7xl gap-6 px-4 py-12 md:grid-cols-[0.96fr_1.04fr] md:py-16">
        <div v-scroll-reveal="'fade-right'" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft md:p-8">
          <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.about.badge }}</div>
          <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.about.companyName }}</h2>
          <p class="mt-4 text-lg font-medium leading-8 text-stone-800 md:text-xl">{{ content.about.heading }}</p>
          <p class="mt-4 text-base leading-8 text-stone-600 md:text-lg">{{ content.about.description }}</p>
        </div>

        <div v-scroll-reveal-stagger="{ variant: 'fade-left' }" class="grid gap-4">
          <article v-for="point in content.about.points" :key="point.title" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft">
            <div class="flex items-start gap-4">
              <span class="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600">
                <AppIcon :name="point.icon" class="h-7 w-7" />
              </span>
              <div>
                <h3 class="text-xl font-semibold text-stone-900">{{ point.title }}</h3>
                <p class="mt-2 text-sm leading-7 text-stone-600">{{ point.description }}</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section id="policy" class="relative overflow-hidden bg-gradient-to-b from-orange-50/80 via-white to-stone-50 py-12 md:py-16">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,126,0,0.12),_transparent_42%)]" />

        <div class="relative mx-auto max-w-7xl px-4">
          <div v-scroll-reveal="'scale-up'" class="overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand-500 via-orange-500 to-red-500 p-6 text-white shadow-float md:p-8 lg:p-10">
            <div class="grid gap-6 md:grid-cols-[1.05fr_0.95fr] md:items-center">
              <div>
                <div class="text-sm font-semibold uppercase tracking-[0.24em] text-white/80">{{ content.policy.badge }}</div>
                <h2 class="mt-3 text-3xl font-semibold md:text-4xl">{{ content.policy.heading }}</h2>
                <p class="mt-5 text-base leading-8 text-white/90 md:text-lg">{{ content.policy.description }}</p>
              </div>

              <figure class="overflow-hidden rounded-[1.5rem] border border-white/20 bg-white/10 shadow-float">
                <img
                  :src="content.policy.image"
                  :alt="content.policy.imageAlt"
                  class="h-48 w-full object-cover md:h-full md:min-h-[220px] lg:min-h-[260px]"
                />
              </figure>
            </div>
          </div>

          <div v-scroll-reveal-stagger class="mt-6 grid gap-4 md:grid-cols-3">
            <article
              v-for="(item, index) in content.policy.items"
              :key="item.title"
              class="group relative rounded-[2rem] border border-stone-200/80 bg-white p-6 shadow-soft transition duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-float md:p-7"
            >
              <div class="flex items-start justify-between gap-4">
                <span class="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600 transition group-hover:bg-brand-500 group-hover:text-white">
                  <AppIcon :name="item.icon" class="h-7 w-7" />
                </span>
                <span class="text-3xl font-semibold leading-none text-stone-200 transition group-hover:text-brand-100">
                  {{ String(index + 1).padStart(2, "0") }}
                </span>
              </div>
              <h3 class="mt-5 text-xl font-semibold text-stone-900">{{ item.title }}</h3>
              <p class="mt-3 text-sm leading-7 text-stone-600">{{ item.description }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="services" class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div v-scroll-reveal class="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.services.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.services.heading }}</h2>
            <p class="mt-4 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.services.description }}</p>
          </div>
          <div class="rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-600 shadow-soft">
            {{ content.services.note }}
          </div>
        </div>

        <div v-scroll-reveal="{ delay: 80 }" class="mb-6 overflow-hidden rounded-[2rem] border border-brand-200 bg-gradient-to-r from-brand-50 via-orange-50 to-white p-6 shadow-soft md:p-8">
          <div class="grid gap-6 lg:grid-cols-[1fr_1.05fr] lg:items-center">
            <div>
              <div class="text-sm font-semibold uppercase tracking-[0.2em] text-brand-600">{{ content.services.household.badge }}</div>
              <h3 class="mt-2 text-xl font-semibold text-stone-900 md:text-2xl">{{ content.services.household.heading }}</h3>
              <p class="mt-3 text-sm leading-7 text-stone-600 md:text-base">{{ content.services.household.description }}</p>
              <a
                href="#contact"
                class="mt-5 inline-flex shrink-0 items-center justify-center rounded-full bg-brand-500 px-6 py-3.5 font-semibold text-white shadow-float transition hover:bg-brand-600"
              >
                {{ content.services.household.cta }}
              </a>
            </div>

            <div v-scroll-reveal-stagger="{ step: 80, variant: 'scale-up' }" class="grid grid-cols-3 gap-2 md:gap-3">
              <figure
                v-for="image in content.services.household.images"
                :key="image.src"
                class="overflow-hidden rounded-[1rem] border border-brand-100 bg-white shadow-soft md:rounded-[1.2rem]"
              >
                <img :src="image.src" :alt="image.alt" class="aspect-[4/5] w-full object-cover" />
              </figure>
            </div>
          </div>
        </div>

        <div v-scroll-reveal-stagger class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <article v-for="service in content.services.items" :key="service.title" class="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:border-brand-200">
            <div class="grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600">
              <AppIcon :name="service.icon" class="h-7 w-7" />
            </div>
            <h3 class="mt-5 text-xl font-semibold text-stone-900">{{ service.title }}</h3>
            <p class="mt-3 text-sm leading-7 text-stone-600">{{ service.description }}</p>
            <ul class="mt-4 grid gap-2 text-sm text-stone-500">
              <li v-for="point in service.points" :key="point" class="flex gap-2">
                <span class="text-brand-500">•</span>
                <span>{{ point }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section id="procurement-support" class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-7xl px-4">
          <div class="grid gap-6 md:grid-cols-[0.95fr_1.05fr]">
            <div v-scroll-reveal="'fade-right'" class="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 shadow-soft md:p-8">
              <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.procurement.badge }}</div>
              <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.procurement.heading }}</h2>
              <p class="mt-4 text-base leading-8 text-stone-600 md:text-lg">{{ content.procurement.description }}</p>
              <a
                :href="content.procurement.ctaHref"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-6 inline-flex rounded-full bg-brand-500 px-6 py-3.5 font-semibold text-white shadow-float transition hover:bg-brand-600"
              >
                {{ content.procurement.cta }}
              </a>
            </div>

            <div v-scroll-reveal-stagger="{ variant: 'fade-left', step: 100 }" class="relative space-y-4">
              <div
                class="pointer-events-none absolute bottom-6 left-7 top-6 hidden w-px bg-gradient-to-b from-brand-200 via-brand-400 to-brand-200 md:block"
                aria-hidden="true"
              />

              <article
                v-for="(item, index) in content.procurement.items"
                :key="item.title"
                class="group relative flex gap-4 md:gap-5"
              >
                <div class="relative z-10 shrink-0 pt-1">
                  <span
                    class="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-orange-500 text-white shadow-float ring-4 ring-white transition duration-300 group-hover:scale-105 group-hover:shadow-[0_16px_40px_rgba(255,126,0,0.28)]"
                  >
                    <AppIcon name="check" class="h-6 w-6" />
                  </span>
                </div>

                <div
                  class="relative flex-1 overflow-hidden rounded-[2rem] border border-stone-200 bg-white p-5 shadow-soft transition duration-300 group-hover:-translate-y-1 group-hover:border-brand-200 group-hover:shadow-float md:p-6"
                >
                  <div
                    class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-brand-400 to-orange-400 opacity-0 transition duration-300 group-hover:opacity-100"
                    aria-hidden="true"
                  />

                  <div class="flex items-start justify-between gap-4">
                    <h3 class="text-lg font-semibold text-stone-900 md:text-xl">{{ item.title }}</h3>
                    <span class="text-2xl font-semibold leading-none text-stone-200 transition duration-300 group-hover:text-brand-100">
                      {{ String(index + 1).padStart(2, "0") }}
                    </span>
                  </div>

                  <p class="mt-3 text-sm leading-7 text-stone-600">{{ item.description }}</p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section id="works" class="bg-white py-12 md:py-16">
        <div class="mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mb-8">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.works.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.works.heading }}</h2>
            <p class="mt-4 max-w-3xl text-base leading-8 text-stone-600 md:text-lg">{{ content.works.description }}</p>
          </div>

          <div v-scroll-reveal-stagger="{ variant: 'scale-up' }" class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article v-for="work in content.works.items" :key="work.title" class="overflow-hidden rounded-[2rem] border border-stone-200 bg-white shadow-soft">
              <img :src="work.image" :alt="work.imageAlt || work.title" class="h-52 w-full object-cover" />
              <div class="p-5">
                <div class="inline-flex rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-brand-700">
                  {{ work.badge }}
                </div>
                <h3 class="mt-4 text-xl font-semibold text-stone-900">{{ work.title }}</h3>
                <p class="mt-2 text-sm leading-7 text-stone-600">{{ work.description }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="gallery" class="relative overflow-hidden bg-gradient-to-b from-white via-orange-50/40 to-white py-12 md:py-16">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,126,0,0.08),_transparent_40%)]" />

        <div class="relative mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mb-8 max-w-3xl md:mb-10">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.gallery.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.gallery.heading }}</h2>
            <p class="mt-4 text-base leading-8 text-stone-600 md:text-lg">{{ content.gallery.description }}</p>
          </div>

          <div
            v-scroll-reveal-stagger="{ variant: 'scale-up', step: 45 }"
            class="grid grid-cols-2 gap-3 md:grid-cols-6 md:auto-rows-[10.5rem] md:gap-4 lg:auto-rows-[11.5rem]"
          >
            <figure
              v-for="(item, index) in content.gallery.items"
              :key="item.image"
              :class="getGalleryLayoutClass(index)"
              class="relative overflow-hidden rounded-[1.25rem] border border-stone-200/80 bg-stone-100 shadow-soft md:rounded-[1.75rem]"
            >
              <button
                type="button"
                class="block h-full w-full cursor-zoom-in"
                :aria-label="content.gallery.openImageLabel.replace('{title}', item.title)"
                @click="openGalleryLightbox(index)"
              >
                <img
                  :src="item.image"
                  :alt="item.imageAlt || item.title"
                  class="h-full w-full object-cover"
                />
              </button>
            </figure>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div v-scroll-reveal class="mb-8 text-center">
          <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.highlights.badge }}</div>
          <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.highlights.heading }}</h2>
        </div>

        <div v-scroll-reveal="{ delay: 80 }" class="relative rounded-[2rem] border border-stone-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-5 shadow-soft md:p-8">
          <div
            class="pointer-events-none absolute left-8 top-24 bottom-24 w-px bg-gradient-to-b from-brand-200 via-brand-400 to-brand-200 md:hidden"
            aria-hidden="true"
          />
          <div
            class="pointer-events-none absolute left-[12%] right-[12%] top-[3.25rem] hidden h-px bg-gradient-to-r from-brand-200 via-brand-400 to-brand-200 md:block"
            aria-hidden="true"
          />

          <div v-scroll-reveal-stagger="{ step: 90, variant: 'fade-up' }" class="flex flex-col md:flex-row md:items-stretch">
            <template v-for="(step, index) in content.highlights.steps" :key="step.label">
              <article class="relative flex flex-1 flex-col md:min-w-0">
                <div class="relative z-10 flex items-start gap-4 md:flex-col md:items-center md:text-center">
                  <div
                    class="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 via-orange-500 to-red-500 text-lg font-semibold text-white shadow-float ring-4 ring-white"
                  >
                    {{ String(index + 1).padStart(2, "0") }}
                  </div>

                  <div class="min-w-0 flex-1 rounded-[1.5rem] border border-stone-200/80 bg-white p-5 shadow-soft md:mt-5 md:w-full md:p-6">
                    <div class="text-sm font-semibold uppercase tracking-[0.16em] text-brand-600">{{ step.value }}</div>
                    <h3 class="mt-2 text-lg font-semibold text-stone-900 md:text-xl">{{ step.label }}</h3>
                    <p class="mt-3 text-sm leading-7 text-stone-600">{{ step.description }}</p>
                  </div>
                </div>
              </article>

              <div
                v-if="index < content.highlights.steps.length - 1"
                class="relative z-10 flex shrink-0 items-center justify-center py-3 pl-7 md:px-3 md:py-0 md:pl-0 md:pt-[3.25rem]"
                aria-hidden="true"
              >
                <span class="grid h-10 w-10 place-items-center rounded-full border border-brand-200 bg-white text-brand-600 shadow-soft md:rotate-0">
                  <AppIcon name="chevron-right" class="h-5 w-5 rotate-90 md:rotate-0" />
                </span>
              </div>
            </template>
          </div>
        </div>
      </section>

      <section id="partners" class="relative overflow-hidden bg-gradient-to-b from-stone-50 via-white to-orange-50/40 py-12 md:py-16">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,126,0,0.1),_transparent_45%)]" />

        <div class="relative mx-auto max-w-7xl px-4">
          <div v-scroll-reveal class="mx-auto mb-10 max-w-3xl text-center">
            <div class="text-sm font-semibold uppercase tracking-[0.24em] text-brand-600">{{ content.partners.badge }}</div>
            <h2 class="mt-3 text-3xl font-semibold text-stone-900 md:text-4xl">{{ content.partners.heading }}</h2>
            <p class="mt-4 text-base leading-8 text-stone-600 md:text-lg">{{ content.partners.description }}</p>
          </div>

          <div v-scroll-reveal-stagger="{ step: 70, variant: 'fade-up' }" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="(partner, index) in content.partners.items"
              :key="partner.title"
              class="relative overflow-hidden rounded-[2rem] border border-stone-200/80 bg-white p-6 shadow-soft md:p-7"
            >
              <div
                class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-400 via-orange-400 to-red-400"
                aria-hidden="true"
              />
              <div class="pointer-events-none absolute -right-2 -top-3 text-6xl font-semibold leading-none text-stone-100">
                {{ String(index + 1).padStart(2, "0") }}
              </div>

              <span class="relative grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-50 via-orange-50 to-white text-brand-600 ring-1 ring-brand-100">
                <AppIcon :name="partner.icon" class="h-7 w-7" />
              </span>

              <h3 class="relative mt-5 text-lg font-semibold leading-8 text-stone-900 md:text-xl">{{ partner.title }}</h3>

              <div class="relative mt-4 flex items-center gap-2 text-sm font-medium text-brand-600">
                <span class="grid h-5 w-5 place-items-center rounded-full bg-brand-50">
                  <AppIcon name="check" class="h-3 w-3" />
                </span>
                {{ content.partners.itemNote }}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="contact" class="mx-auto max-w-7xl px-4 py-12 md:py-16">
        <div v-scroll-reveal="'scale-up'" class="overflow-hidden rounded-[2rem] bg-stone-900 px-6 py-10 text-white shadow-float md:px-10 md:py-14">
          <div class="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div v-scroll-reveal="'fade-right'">
              <div class="text-sm font-semibold uppercase tracking-[0.24em] text-orange-300">{{ content.contact.badge }}</div>
              <h2 class="mt-3 text-3xl font-semibold md:text-5xl">{{ content.contact.heading }}</h2>
              <p class="mt-5 max-w-2xl text-base leading-8 text-white/80 md:text-lg">{{ content.contact.description }}</p>

              <div v-scroll-reveal-stagger="{ step: 80 }" class="mt-6 grid gap-4 sm:grid-cols-2">
                <div v-for="item in content.contact.items" :key="item.label" class="rounded-[1.6rem] border border-white/10 bg-white/5 p-5">
                  <div class="text-sm font-medium uppercase tracking-[0.14em] text-orange-200">{{ item.label }}</div>
                  <div class="mt-2 text-lg font-semibold text-white">{{ item.value }}</div>
                  <div class="mt-2 text-sm leading-7 text-white/70">{{ item.description }}</div>
                </div>
              </div>
            </div>

            <div v-scroll-reveal="{ variant: 'fade-left', delay: 120 }" class="rounded-[1.6rem] border border-white/10 bg-white/5 p-5 md:p-6">
              <div class="text-xs font-semibold uppercase tracking-[0.16em] text-[#06c755]">{{ content.contact.line.badge }}</div>
              <h3 class="mt-3 text-xl font-semibold text-white md:text-2xl">{{ content.contact.line.heading }}</h3>
              <p class="mt-3 text-sm leading-7 text-white/70 md:text-base">{{ content.contact.line.description }}</p>

              <div class="mt-5 rounded-[1.2rem] border border-white/10 bg-black/20 p-4">
                <div class="text-xs font-semibold uppercase tracking-[0.16em] text-[#06c755]">{{ content.contact.line.accountLabel }}</div>
                <div class="mt-2 text-lg font-semibold text-white">{{ content.contact.line.account }}</div>
                <div class="mt-2 text-sm leading-7 text-white/70">{{ content.contact.line.helper }}</div>
              </div>

              <div class="mt-4 flex justify-center">
                <img
                  :src="content.contact.line.qrImage"
                  :alt="content.contact.line.qrAlt"
                  class="h-40 w-40 rounded-2xl bg-white p-2 md:h-44 md:w-44"
                />
              </div>

              <a
                :href="content.contact.line.href"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-5 inline-flex w-full items-center justify-center rounded-full bg-[#06c755] px-6 py-3.5 text-base font-semibold text-white shadow-float transition hover:brightness-95"
              >
                {{ content.contact.line.cta }}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <GalleryLightbox
      :open="galleryLightboxIndex !== null"
      :index="galleryLightboxIndex ?? 0"
      :items="content.gallery.items"
      :labels="content.gallery.lightbox"
      @close="closeGalleryLightbox"
      @update:index="galleryLightboxIndex = $event"
    />

    <SiteFooter :nav-items="navItems" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { LINE_ID, LINE_QR_IMAGE, LINE_URL } from "../constants/line";
import AppIcon from "../components/AppIcon.vue";
import FloatingRightMenu from "../components/FloatingRightMenu.vue";
import GalleryLightbox from "../components/GalleryLightbox.vue";
import SiteFooter from "../components/SiteFooter.vue";
import SiteHeader from "../components/SiteHeader.vue";

const { locale } = useI18n();

const navItems = [
  { key: "home", label: { th: "หน้าแรก", en: "Home" }, href: "/", to: "/" },
  { key: "about", label: { th: "เกี่ยวกับเรา", en: "About" }, href: "/#about" },
  { key: "services", label: { th: "บริการ", en: "Services" }, href: "/#services" },
  { key: "works", label: { th: "ผลงาน", en: "Works" }, href: "/#works" },
  { key: "contact", label: { th: "ติดต่อเรา", en: "Contact" }, href: "/#contact" }
];

const floatingMenuItems = [
  { key: "services", label: { th: "ดูบริการ", en: "Services" }, href: "/#services", icon: "grid" },
  { key: "works", label: { th: "ดูผลงาน", en: "Works" }, href: "/#works", icon: "ticket" },
  { key: "line", label: { th: "เพิ่มเพื่อน", en: "Add friend" }, href: LINE_URL, icon: "heart" }
];

const galleryAssets = [
  { file: "gallery-10-traffic-vest.jpg", title: { th: "เสื้อสะท้อนแสงและอุปกรณ์จราจร", en: "Reflective vests and traffic gear" } },
  { file: "gallery-09-traffic-cone.jpg", title: { th: "กรวยจราจรและอุปกรณ์จราจร", en: "Traffic cones and traffic equipment" } },
  { file: "gallery-01-fire-extinguisher.jpg", title: { th: "ถังดับเพลิง", en: "Fire extinguishers" } },
  { file: "gallery-02-fire-hose.jpg", title: { th: "สายดับเพลิงและอุปกรณ์ดับเพลิง", en: "Fire hoses and firefighting equipment" } },
  { file: "gallery-03-fire-alarm.jpg", title: { th: "อุปกรณ์แจ้งเตือนอัคคีภัย", en: "Fire alarm equipment" } },
  { file: "gallery-04-safety-sign.jpg", title: { th: "ป้ายเตือนและป้ายความปลอดภัย", en: "Safety and warning signs" } },
  { file: "gallery-05-ppe.jpg", title: { th: "อุปกรณ์ป้องกันส่วนบุคคล", en: "Personal protective equipment" } },
  { file: "gallery-06-office-supplies.jpg", title: { th: "วัสดุสำนักงาน", en: "Office supplies" } },
  { file: "gallery-07-office-furniture.jpg", title: { th: "ครุภัณฑ์และเฟอร์นิเจอร์สำนักงาน", en: "Office furniture" } },
  { file: "gallery-08-stationery.jpg", title: { th: "เครื่องเขียนและอุปกรณ์ใช้งานทั่วไป", en: "Stationery and general supplies" } },
  { file: "gallery-11-rescue-kit.jpg", title: { th: "อุปกรณ์กู้ชีพและปฐมพยาบาล", en: "First aid and rescue kits" } },
  { file: "gallery-12-emergency-equipment.jpg", title: { th: "อุปกรณ์ฉุกเฉิน", en: "Emergency equipment" } },
  { file: "gallery-13-kitchen-supplies.jpg", title: { th: "อุปกรณ์ครัวและของใช้ในครัว", en: "Kitchen and household items" } },
  { file: "gallery-14-daily-use.jpg", title: { th: "ของใช้ในบ้านและใช้งานประจำวัน", en: "Daily home-use products" } },
  { file: "gallery-15-factory-safety.jpg", title: { th: "อุปกรณ์เซฟตี้ในโรงงาน", en: "Factory safety equipment" } },
  { file: "gallery-16-warehouse-supplies.jpg", title: { th: "อุปกรณ์คลังสินค้าและพื้นที่ปฏิบัติงาน", en: "Warehouse and operational supplies" } },
  { file: "gallery-17-school-supplies.jpg", title: { th: "ครุภัณฑ์และอุปกรณ์สำหรับสถาบัน", en: "School and institution supplies" } },
  { file: "gallery-18-field-support.jpg", title: { th: "อุปกรณ์สนับสนุนหน้างาน", en: "Field support equipment" } },
  { file: "gallery-19-building-maintenance.jpg", title: { th: "อุปกรณ์ดูแลอาคารและสถานที่", en: "Building maintenance supplies" } },
  { file: "gallery-20-procurement-mix.jpg", title: { th: "สินค้าหลายหมวดตามการจัดหา", en: "Mixed procurement categories" } }
];

const galleryLayoutClasses = [
  "col-span-2 aspect-[16/10] md:col-span-3 md:row-span-2 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-2 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-2 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-2 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-2 md:row-span-1 md:aspect-auto",
  "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-1 md:row-span-1 md:aspect-auto",
  "col-span-2 aspect-[16/10] md:col-span-2 md:row-span-1 md:aspect-auto"
];

const getGalleryLayoutClass = (index) => galleryLayoutClasses[index] || "col-span-1 aspect-square md:col-span-1 md:row-span-1 md:aspect-auto";

const buildGalleryItems = (lang) =>
  galleryAssets.map((item) => ({
    title: item.title[lang],
    image: `/images/gallery/${item.file}`,
    imageAlt: item.title[lang]
  }));

const partnerAssets = [
  { icon: "grid", title: { th: "สำนักงานและออฟฟิศ", en: "Offices and workplaces" } },
  { icon: "store", title: { th: "โรงงานและคลังสินค้า", en: "Factories and warehouses" } },
  { icon: "users", title: { th: "โรงเรียนและสถาบัน", en: "Schools and institutions" } },
  { icon: "shield-check", title: { th: "หน่วยงานราชการและโครงการ", en: "Public agencies and projects" } },
  { icon: "tag", title: { th: "สถานประกอบการและอาคาร", en: "Facilities and buildings" } },
  { icon: "truck", title: { th: "งานภาคสนามและงานสนับสนุน", en: "Field and support operations" } }
];

const buildPartnerItems = (lang) =>
  partnerAssets.map((item) => ({
    icon: item.icon,
    title: item.title[lang]
  }));

const homeContent = {
  th: {
    hero: {
      badge: "Thitima Panich",
      heading: "จำหน่ายและจัดหาอุปกรณ์สำนักงาน ครุภัณฑ์ อุปกรณ์เซฟตี้ ดับเพลิง กู้ชีพ กู้ภัย และวัสดุสำหรับองค์กร",
      subheading: "พร้อมให้คำปรึกษา แนะนำสินค้า และประสานงานจัดซื้อให้เหมาะกับลักษณะการใช้งานจริงของแต่ละหน่วยงาน",
      description:
        "ฐิติมา พาณิชย์ ให้บริการจำหน่ายและจัดหาอุปกรณ์ที่จำเป็นสำหรับหน่วยงาน องค์กร โรงเรียน โรงงาน สำนักงาน และโครงการต่าง ๆ ครอบคลุมทั้งอุปกรณ์สำนักงาน ครุภัณฑ์ อุปกรณ์ดับเพลิง อุปกรณ์เซฟตี้ กู้ชีพ กู้ภัย วัสดุสำนักงาน อุปกรณ์จราจร ของใช้ในครัวเรือน และสินค้าสนับสนุนอื่น ๆ โดยเน้นความครบถ้วน ความเหมาะสมต่อการใช้งาน และการดูแลลูกค้าอย่างต่อเนื่อง",
      ctaPrimary: "ดูบริการของเรา",
      ctaSecondary: "ติดต่อทีมงาน",
      stats: [
        { value: "ครบ", label: "กลุ่มสินค้าและบริการ" },
        { value: "ต่อเนื่อง", label: "บริการและประสานงาน" },
        { value: "องค์กร", label: "กลุ่มลูกค้าหลัก" }
      ],
      slides: [
        {
          badge: "อุปกรณ์จราจร",
          title: "เสื้อสะท้อนแสงและอุปกรณ์จราจร",
          description: "ภาพตัวอย่าง: เสื้อจราจร กรวยจราจร หรืออุปกรณ์ควบคุมพื้นที่",
          image: "/images/hero/slide-03-traffic-safety.jpg"
        },
        {
          badge: "อุปกรณ์สำนักงาน",
          title: "วัสดุสำนักงานและครุภัณฑ์",
          description: "ภาพตัวอย่าง: วัสดุสำนักงาน อุปกรณ์ใช้งาน หรือบรรยากาศพื้นที่ทำงาน",
          image: "/images/hero/slide-01-office-supply.jpg"
        },
        {
          badge: "ดับเพลิง & เซฟตี้",
          title: "ถังดับเพลิงและอุปกรณ์ความปลอดภัย",
          description: "ภาพตัวอย่าง: ถังดับเพลิง ป้ายเตือน หรืออุปกรณ์เซฟตี้",
          image: "/images/hero/slide-02-fire-safety.jpg"
        }
      ]
    },
    about: {
      badge: "About Us",
      companyName: "ฐิติมา พาณิชย์",
      heading: "ให้ข้อมูลชัดเจน แนะนำสินค้าเหมาะสม และช่วยประสานงานจัดซื้อได้ครบในที่เดียว",
      description:
        "ฐิติมา พาณิชย์ ดูแลสินค้าและบริการหลายกลุ่ม เช่น อุปกรณ์สำนักงาน วัสดุสำนักงาน ครุภัณฑ์สำนักงาน อุปกรณ์ดับเพลิง อุปกรณ์เซฟตี้ อุปกรณ์กู้ชีพกู้ภัย อุปกรณ์จราจร ของใช้ในครัวเรือน รวมถึงสินค้าสนับสนุนงานทั่วไปสำหรับหน่วยงานต่าง ๆ โดยเน้นการช่วยคัดกรองความต้องการของลูกค้าและแนะนำหมวดหมู่สินค้าให้เหมาะกับการใช้งานจริง",
      points: [
        {
          icon: "users",
          title: "ช่วยคัดกรองความต้องการเบื้องต้น",
          description: "ลูกค้าไม่จำเป็นต้องทราบรายการทั้งหมดตั้งแต่ต้น สามารถเริ่มจากลักษณะงานหรือประเภทพื้นที่ แล้วให้เราช่วยวิเคราะห์ความต้องการเบื้องต้นได้"
        },
        {
          icon: "shield-check",
          title: "แนะนำหมวดหมู่สินค้าอย่างเหมาะสม",
          description: "ช่วยจัดกลุ่มอุปกรณ์ที่เกี่ยวข้อง เช่น อุปกรณ์สำนักงาน เซฟตี้ ดับเพลิง จราจร หรือครุภัณฑ์ เพื่อให้ลูกค้าเห็นภาพรวมก่อนจัดซื้อ"
        },
        {
          icon: "globe",
          title: "ดูแลและประสานงานต่อเนื่อง",
          description: "พร้อมให้ข้อมูลเพิ่มเติม ติดตามรายการ และประสานงานจนลูกค้าได้รับสินค้าที่เหมาะกับความต้องการ"
        }
      ]
    },
    policy: {
      badge: "Service Direction",
      heading: "แนวทางการดูแลงานของเรา",
      description: "เราเน้นการจำหน่าย จัดหา ให้คำปรึกษา และประสานงานจัดซื้ออย่างเป็นระบบ เพื่อช่วยให้ลูกค้าได้รายการที่เหมาะกับการใช้งานจริง ครบถ้วน และตรงความต้องการของแต่ละหน่วยงาน",
      image: "/images/sections/policy-service.jpg",
      imageAlt: "ทีมงานให้คำปรึกษาและประสานงานจัดหาอุปกรณ์",
      items: [
        {
          icon: "phone",
          title: "ดูแลตั้งแต่การสอบถามเบื้องต้น",
          description: "รับข้อมูลลักษณะงาน พื้นที่ใช้งาน และรายการที่ต้องการ เพื่อช่วยลูกค้าจัดลำดับความสำคัญและเห็นภาพรวมก่อนเริ่มจัดซื้อ"
        },
        {
          icon: "grid",
          title: "ช่วยรวบรวมและจัดกลุ่มรายการสินค้า",
          description: "เหมาะสำหรับงานที่มีหลายหมวดพร้อมกัน เช่น งานสำนักงาน ครุภัณฑ์ งานความปลอดภัย ของใช้ในครัวเรือน และอุปกรณ์สนับสนุนอื่น ๆ"
        },
        {
          icon: "refresh",
          title: "พร้อมประสานงานต่อเนื่องจนจบงาน",
          description: "ให้ข้อมูลเพิ่มเติม ติดตามความต้องการ และช่วยประสานงานเพื่อให้ลูกค้าได้รับสินค้าที่ตรงกับงบประมาณและการใช้งาน"
        }
      ]
    },
    services: {
      badge: "Our Services",
      heading: "บริการและกลุ่มสินค้าที่เราดูแล",
      description: "เราจำหน่ายและจัดหาอุปกรณ์สำหรับสำนักงาน หน่วยงาน โรงงาน อาคาร และงานความปลอดภัย รวมถึงของใช้ในครัวเรือนและสินค้าใช้งานทั่วไป หากยังไม่แน่ใจว่าควรเลือกอะไร สามารถปรึกษาเราก่อนได้",
      note: "จำหน่าย จัดหา ให้คำปรึกษา และประสานงานจัดซื้อ",
      household: {
        badge: "Household Items",
        heading: "ของใช้ในครัวเรือนและของใช้ทั่วไป จัดหาได้เช่นกัน",
        description: "นอกจากอุปกรณ์สำหรับองค์กรแล้ว เรายังช่วยจัดหาของใช้ในครัวเรือน อุปกรณ์ครัว และรายการใช้งานทั่วไปได้ หากยังไม่แน่ใจว่าควรเลือกอะไร ยินดีให้คุณส่งรายการหรือพูดคุยเบื้องต้นกับเราก่อน เราจะช่วยแนะนำและประสานงานจัดซื้อให้เหมาะกับความต้องการ",
        cta: "ปรึกษาก่อนจัดซื้อ",
        images: [
          {
            src: "/images/household/household-01-kitchen.jpg",
            alt: "อุปกรณ์ครัวและของใช้ในครัว"
          },
          {
            src: "/images/household/household-02-daily-use.jpg",
            alt: "ของใช้ในบ้านและใช้งานประจำวัน"
          },
          {
            src: "/images/household/household-03-general.jpg",
            alt: "สินค้าใช้งานทั่วไปตามรายการที่ต้องการ"
          }
        ]
      },
      items: [
        {
          icon: "grid",
          title: "สำนักงานและหน่วยงาน",
          description: "วัสดุสำนักงาน อุปกรณ์ใช้งานประจำวัน และครุภัณฑ์สำหรับพื้นที่ทำงาน",
          points: ["เครื่องเขียนและวัสดุใช้งานทั่วไป", "อุปกรณ์สำนักงานประจำวัน", "ครุภัณฑ์สำหรับพื้นที่ทำงาน"]
        },
        {
          icon: "store",
          title: "โรงงานและสถานประกอบการ",
          description: "อุปกรณ์เซฟตี้ ดับเพลิง ป้ายเตือน และอุปกรณ์สนับสนุนด้านความปลอดภัย",
          points: ["อุปกรณ์ป้องกันและเซฟตี้", "ดับเพลิงและป้ายเตือน", "สินค้าสนับสนุนด้านความปลอดภัย"]
        },
        {
          icon: "shield-check",
          title: "โรงเรียนและสถาบัน",
          description: "อุปกรณ์สำนักงาน ครุภัณฑ์ และของใช้จำเป็นสำหรับการบริหารงานและพื้นที่ส่วนกลาง",
          points: ["วัสดุสำนักงานและอุปกรณ์ทั่วไป", "ครุภัณฑ์สำหรับพื้นที่ใช้งาน", "ของใช้จำเป็นในพื้นที่ส่วนกลาง"]
        },
        {
          icon: "truck",
          title: "งานจราจร กู้ชีพ กู้ภัย",
          description: "อุปกรณ์จราจร อุปกรณ์กู้ชีพ กู้ภัย และอุปกรณ์เฉพาะทางตามความต้องการของลูกค้า",
          points: ["กรวยจราจรและอุปกรณ์ควบคุมพื้นที่", "อุปกรณ์กู้ชีพและกู้ภัย", "รายการเฉพาะทางตามลักษณะงาน"]
        },
        {
          icon: "shopping-bag",
          title: "ของใช้ในครัวเรือน",
          description: "ช่วยจัดหาของใช้ในบ้าน อุปกรณ์ครัว และสินค้าใช้งานทั่วไป ตามรายการที่ต้องการ",
          points: ["อุปกรณ์และของใช้ในครัว", "ของใช้ในบ้านและใช้งานประจำวัน", "รายการทั่วไปตามที่ลูกค้าต้องการ"]
        }
      ]
    },
    procurement: {
      badge: "Procurement Support",
      heading: "บริการช่วยจัดหาและรวบรวมรายการสินค้า",
      description:
        "หากลูกค้ายังไม่แน่ใจว่าต้องใช้อุปกรณ์ประเภทใด มีหลายหมวดสินค้าที่ต้องจัดซื้อพร้อมกัน หรือต้องการของใช้ในครัวเรือนและรายการทั่วไป สามารถปรึกษาเราก่อนได้ เรายินดีช่วยแนะนำรายการที่เหมาะสม จัดกลุ่มความต้องการ และช่วยให้เห็นภาพรวมก่อนเริ่มจัดซื้อ",
      cta: "เพิ่มเพื่อน LINE",
      ctaHref: LINE_URL,
      items: [
        {
          title: "เริ่มต้นได้แม้ยังไม่สรุปรายการ",
          description: "หากยังไม่แน่ใจว่าต้องใช้อะไรบ้าง สามารถเริ่มจากลักษณะพื้นที่หรือประเภทงาน แล้วให้เราช่วยคัดรายการที่เหมาะสมได้"
        },
        {
          title: "ช่วยจัดกลุ่มความต้องการให้ชัดขึ้น",
          description: "เหมาะสำหรับงานที่มีหลายหมวดพร้อมกัน เช่น สำนักงาน ครุภัณฑ์ ความปลอดภัย ดับเพลิง และงานสนับสนุนภาคสนาม"
        },
        {
          title: "ช่วยให้ตัดสินใจจัดซื้อได้สะดวกขึ้น",
          description: "เราพร้อมให้ข้อมูลเพิ่มเติม ติดตามรายการ และประสานงานต่อ เพื่อให้ลูกค้าเห็นภาพรวมและเดินงานได้ง่ายขึ้น"
        }
      ]
    },
    works: {
      badge: "Support Areas",
      heading: "กลุ่มงานที่เหมาะกับบริการของเรา",
      description: "เราจำหน่ายและจัดหาอุปกรณ์ให้เหมาะกับลักษณะงานของแต่ละองค์กร ทั้งสำนักงาน พื้นที่ปฏิบัติงาน งานความปลอดภัย และงานสนับสนุนภาคสนาม",
      items: [
        {
          badge: "Office",
          title: "งานสำนักงานและพื้นที่บริหาร",
          description: "เหมาะกับการจัดหาวัสดุสำนักงาน ครุภัณฑ์ และอุปกรณ์ที่ใช้ในพื้นที่ทำงานและงานธุรการ",
          image: "/images/works/works-01-office.jpg",
          imageAlt: "งานสำนักงานและพื้นที่บริหาร"
        },
        {
          badge: "Safety",
          title: "งานด้านความปลอดภัยและอัคคีภัย",
          description: "รองรับอุปกรณ์เซฟตี้ ดับเพลิง ป้ายเตือน และสินค้าที่เกี่ยวข้องกับการดูแลความปลอดภัยของพื้นที่",
          image: "/images/works/works-02-safety-fire.jpg",
          imageAlt: "งานด้านความปลอดภัยและอัคคีภัย"
        },
        {
          badge: "Operations",
          title: "งานภาคสนามและพื้นที่ควบคุม",
          description: "ดูแลอุปกรณ์จราจร กู้ชีพ กู้ภัย และรายการเฉพาะที่เหมาะกับหน้างานจริง",
          image: "/images/works/works-03-field-operations.jpg",
          imageAlt: "งานภาคสนามและพื้นที่ควบคุม"
        },
        {
          badge: "Corporate",
          title: "งานจัดซื้อหลายหมวดในครั้งเดียว",
          description: "เหมาะสำหรับลูกค้าที่ต้องการรวบรวมรายการหลายประเภทและต้องการผู้ช่วยประสานงานให้ภาพรวมชัดเจนขึ้น",
          image: "/images/works/works-04-multi-category.jpg",
          imageAlt: "งานจัดซื้อหลายหมวดในครั้งเดียว"
        }
      ]
    },
    gallery: {
      badge: "Gallery",
      heading: "ภาพตัวอย่างสินค้าและงานที่เราดูแล",
      description: "ตัวอย่างกลุ่มสินค้าและงานจัดหาที่เรารองรับ ครอบคลุมทั้งงานสำนักงาน ความปลอดภัย ดับเพลิง จราจร กู้ชีพ ของใช้ในครัวเรือน และรายการใช้งานทั่วไป",
      openImageLabel: "ดูภาพ {title}",
      lightbox: {
        viewer: "ดูภาพสินค้า",
        close: "ปิด",
        previous: "ภาพก่อนหน้า",
        next: "ภาพถัดไป"
      },
      items: buildGalleryItems("th")
    },
    highlights: {
      badge: "Service Flow",
      heading: "แนวทางดูแลลูกค้าแบบเป็นขั้นตอน",
      steps: [
        { value: "ครบถ้วน", label: "กลุ่มสินค้า", description: "ครอบคลุมทั้งงานสำนักงาน ครุภัณฑ์ ดับเพลิง เซฟตี้ และงานสนับสนุน" },
        { value: "ต่อเนื่อง", label: "บริการและประสานงาน", description: "พร้อมให้ข้อมูล ติดตามรายการ และช่วยประสานงานจนลูกค้าได้สินค้าที่เหมาะสม" },
        { value: "องค์กร", label: "กลุ่มลูกค้าหลัก", description: "เหมาะกับสำนักงาน โรงงาน โรงเรียน หน่วยงานราชการ และโครงการต่าง ๆ" },
        { value: "สะดวก", label: "สอบถามและจัดหาในที่เดียว", description: "ช่วยลดขั้นตอนเมื่อต้องจัดซื้อหลายหมวดพร้อมกันและต้องการข้อมูลให้ครบก่อนตัดสินใจ" }
      ]
    },
    partners: {
      badge: "กลุ่มลูกค้าที่ดูแล",
      heading: "พร้อมให้บริการลูกค้าหลากหลายประเภท",
      description: "เราจำหน่ายและจัดหาอุปกรณ์ให้เหมาะกับลักษณะการใช้งานของแต่ละหน่วยงาน ทั้งงานสำนักงาน งานความปลอดภัย งานสนับสนุน และงานเฉพาะทาง",
      itemNote: "รองรับการจัดหาและประสานงาน",
      items: buildPartnerItems("th")
    },
    contact: {
      badge: "Contact Us",
      heading: "ติดต่อสอบถามและส่งรายละเอียดงาน",
      description: "หากต้องการสอบถามสินค้า ขอคำแนะนำเบื้องต้น หรือมีหลายหมวดที่ต้องการจัดซื้อพร้อมกัน สามารถส่งรายละเอียดงานเข้ามาได้ เราพร้อมช่วยดูข้อมูล ประสานงาน และแนะนำแนวทางที่เหมาะกับการใช้งานจริง",
      items: [
        { label: "Company", value: "ฐิติมา พาณิชย์", description: "จำหน่ายและจัดหาอุปกรณ์สำนักงาน ครุภัณฑ์ อุปกรณ์เซฟตี้ ดับเพลิง กู้ชีพ กู้ภัย ของใช้ในครัวเรือน และสินค้าใช้งานทั่วไป" },
        { label: "Support", value: "คำปรึกษาและประสานงานจัดซื้อ", description: "เหมาะสำหรับลูกค้าที่ต้องการรวบรวมรายการสินค้า สอบถามข้อมูลเพิ่มเติม หรือเริ่มต้นจัดซื้อหลายหมวดในครั้งเดียว" }
      ],
      line: {
        badge: "เพิ่มเพื่อน LINE",
        heading: "เพิ่มเพื่อน LINE เพื่อสอบถามและส่งข้อมูลงาน",
        description: "สแกน QR Code หรือค้นหา LINE ID ด้านล่างเพื่อเพิ่มเพื่อน แล้วส่งรายละเอียดงานหรือพูดคุยเบื้องต้นได้ทันที",
        accountLabel: "LINE ID",
        account: LINE_ID,
        helper: "ค้นหา LINE ID นี้ในแอป LINE แล้วกดเพิ่มเพื่อน เพื่อเริ่มสอบถามหรือส่งรายการที่ต้องการ",
        qrAlt: "LINE QR Code",
        qrImage: LINE_QR_IMAGE,
        cta: "เพิ่มเพื่อน LINE",
        href: LINE_URL
      }
    }
  },
  en: {
    hero: {
      badge: "Thitima Panich",
      heading: "Fire safety, office supply, and equipment support for organizations and facilities",
      subheading: "Ready to recommend and source suitable equipment for real operational use across different organization types",
      description:
        "Thitima Panich supports fire safety equipment, office supplies, office furniture, rescue, first-aid, traffic equipment, household items, and related support categories, with practical recommendations, coordination, and continued after-sales support.",
      ctaPrimary: "Explore Services",
      ctaSecondary: "Contact Team",
      stats: [
        { value: "6+", label: "Core service groups" },
        { value: "Business", label: "Primary customer group" },
        { value: "Ongoing", label: "After-sales support" }
      ],
      slides: [
        {
          badge: "Traffic Equipment",
          title: "Reflective vests and traffic control items",
          description: "Suggested photo: traffic vests, cones, or area-control equipment.",
          image: "/images/hero/slide-03-traffic-safety.jpg"
        },
        {
          badge: "Office Supply",
          title: "Office supplies and furniture",
          description: "Suggested photo: office materials, daily-use items, or workplace atmosphere.",
          image: "/images/hero/slide-01-office-supply.jpg"
        },
        {
          badge: "Fire Safety",
          title: "Fire extinguishers and safety equipment",
          description: "Suggested photo: fire extinguishers, warning signs, or safety gear.",
          image: "/images/hero/slide-02-fire-safety.jpg"
        }
      ]
    },
    about: {
      badge: "About Us",
      companyName: "Thitima Panich",
      heading: "Clear guidance, suitable recommendations, and procurement support in one place",
      description:
        "Thitima Panich supports multiple product groups including office supplies, furniture, fire safety, rescue, traffic, household items, and general support items for organizations. Our role is to help customers screen their needs and identify suitable product groups before purchasing.",
      points: [
        {
          icon: "users",
          title: "Built for organizations",
          description: "The messaging is shaped for companies, schools, factories, public-sector teams, and project-based customers."
        },
        {
          icon: "shield-check",
          title: "Trust-focused presentation",
          description: "The site presents service scope, readiness, and work categories so customers can understand what we support at a glance."
        },
        {
          icon: "globe",
          title: "Clear and direct information",
          description: "The content is arranged so customers can quickly understand the service scope, product groups, and support approach."
        }
      ]
    },
    policy: {
      badge: "Company Policy",
      heading: "Our service and information approach",
      description: "We supply, source, consult, and coordinate procurement in a structured way so customers receive practical, complete lists that fit real operational needs.",
      image: "/images/sections/policy-service.jpg",
      imageAlt: "Consultation and procurement coordination support",
      items: [
        {
          icon: "store",
          title: "Service-led approach",
          description: "We present services, product groups, and coordination support clearly so customers can understand our scope and start with confidence."
        },
        {
          icon: "shield-check",
          title: "Scope and suitability first",
          description: "Visitors can quickly see what the company supports and which organization types the service is designed for."
        },
        {
          icon: "phone",
          title: "Direct consultation and coordination",
          description: "Customers can ask questions, discuss requirements, and coordinate procurement through a direct communication channel."
        }
      ]
    },
    services: {
      badge: "Our Services",
      heading: "Service and product groups we support",
      description: "We supply and source equipment for offices, organizations, factories, facilities, and safety-related operations, including household and kitchen items. If you are unsure what to choose, consult us first.",
      note: "Supply, sourcing, consultation, and procurement coordination",
      household: {
        badge: "Household Items",
        heading: "Household and kitchen items are also available",
        description: "In addition to organization-focused equipment, we can help source household goods, kitchen items, and general daily-use products. If you are not sure what to order yet, send us your list or start with a brief consultation and we will help recommend suitable options.",
        cta: "Consult before purchasing",
        images: [
          {
            src: "/images/household/household-01-kitchen.jpg",
            alt: "Kitchen tools and household supplies"
          },
          {
            src: "/images/household/household-02-daily-use.jpg",
            alt: "Daily home-use items"
          },
          {
            src: "/images/household/household-03-general.jpg",
            alt: "General products by customer request"
          }
        ]
      },
      items: [
        {
          icon: "grid",
          title: "Office Supplies",
          description: "General-use office supply categories for daily operations and administrative environments.",
          points: ["Stationery and daily-use items", "Meeting accessories and boards", "Document support materials"]
        },
        {
          icon: "store",
          title: "Office Furniture",
          description: "Presentation and coordination of workplace furniture and storage-related categories.",
          points: ["Desks, chairs, and filing cabinets", "Shelving and storage support", "Practical workplace layout guidance"]
        },
        {
          icon: "shield-check",
          title: "Fire, Rescue, and Emergency",
          description: "Appropriate for organizations that need information around safety and emergency equipment categories.",
          points: ["Fire equipment and warning signs", "First-aid and emergency support items", "Safety-oriented categories for facilities"]
        },
        {
          icon: "truck",
          title: "Traffic and Site Support",
          description: "Suitable for outdoor areas, controlled zones, and specialized operational requirements.",
          points: ["Traffic cones and barriers", "Area-control equipment", "Additional sourcing support by request"]
        },
        {
          icon: "shopping-bag",
          title: "Household and Kitchen Items",
          description: "We can help source household goods, kitchen items, and general daily-use products based on your requirement list.",
          points: ["Kitchen tools and household supplies", "General home-use items", "Other everyday products by request"]
        }
      ]
    },
    procurement: {
      badge: "Procurement Support",
      heading: "Support for sourcing and grouping required items",
      description:
        "If the required items are still unclear, the project needs several categories at once, or you need household and general-use products, consult us first. We help group the requirement, suggest suitable item types, and make the overall scope easier to understand.",
      cta: "Add LINE friend",
      ctaHref: LINE_URL,
      items: [
        {
          title: "Start even without a finalized list",
          description: "If you are not sure what you need yet, you can start from the type of space or work, and we will help shortlist suitable items."
        },
        {
          title: "We help organize requirements more clearly",
          description: "Ideal for projects with several categories at once, such as office supplies, furniture, safety, fire protection, and field support."
        },
        {
          title: "We help make purchasing decisions easier",
          description: "We are ready to provide more information, follow up on your list, and coordinate next steps so you see the full picture and move forward smoothly."
        }
      ]
    },
    works: {
      badge: "Support Areas",
      heading: "Work areas suited to our support",
      description: "We help source equipment to match different organization types, from office and administrative use to safety, field support, and multi-category procurement.",
      items: [
        {
          badge: "Office",
          title: "Office and education support",
          description: "Suitable for communicating supply and furniture support for workspaces and shared-use environments.",
          image: "/images/works/works-01-office.jpg",
          imageAlt: "Office and administrative workspace support"
        },
        {
          badge: "Safety",
          title: "Building and facility safety support",
          description: "Useful for presenting fire equipment, signage, and internal safety-related categories.",
          image: "/images/works/works-02-safety-fire.jpg",
          imageAlt: "Safety and fire protection equipment support"
        },
        {
          badge: "Operations",
          title: "Field and controlled-area support",
          description: "A suitable section for traffic, rescue, emergency, and site-specific equipment communication.",
          image: "/images/works/works-03-field-operations.jpg",
          imageAlt: "Field operations and controlled-area support"
        },
        {
          badge: "Corporate",
          title: "Corporate brand communication",
          description: "The refreshed layout and tone present the business as credible, organized, and ready to support customer requirements.",
          image: "/images/works/works-04-multi-category.jpg",
          imageAlt: "Multi-category procurement coordination"
        }
      ]
    },
    gallery: {
      badge: "Gallery",
      heading: "Sample products and support categories",
      description: "Examples of product groups and sourcing work we support, covering office supplies, safety, fire protection, traffic, rescue, household items, and general-use products.",
      openImageLabel: "View {title}",
      lightbox: {
        viewer: "Product image viewer",
        close: "Close",
        previous: "Previous image",
        next: "Next image"
      },
      items: buildGalleryItems("en")
    },
    highlights: {
      badge: "Service Flow",
      heading: "How we support customers step by step",
      steps: [
        { value: "Complete", label: "Product groups", description: "Covers office supplies, furniture, fire safety, safety equipment, and support categories." },
        { value: "Ongoing", label: "Service and coordination", description: "Ready to provide information, follow up on your list, and coordinate until you receive suitable products." },
        { value: "Organizations", label: "Primary customers", description: "Suitable for offices, factories, schools, public agencies, and various projects." },
        { value: "Convenient", label: "One place to inquire and source", description: "Helps reduce steps when purchasing several categories together and needing complete information before deciding." }
      ]
    },
    partners: {
      badge: "Customer Groups",
      heading: "Ready to support many types of customers",
      description: "We supply and source products based on the actual needs of each unit, covering office work, safety requirements, support items, and specialized requests.",
      itemNote: "Sourcing and coordination supported",
      items: buildPartnerItems("en")
    },
    contact: {
      badge: "Contact Us",
      heading: "Contact us and send your requirement details",
      description: "If you need product guidance, want to discuss several categories together, or would like to start procurement coordination, send us your project details and we will help you review the requirement.",
      items: [
        { label: "Company", value: "Thitima Panich", description: "Supply and sourcing support for office, furniture, fire safety, rescue, traffic, household items, and general-use products." },
        { label: "Support", value: "Consultation and procurement coordination", description: "Suitable for customers who need help grouping product lists, asking for more details, or coordinating multi-category purchasing." }
      ],
      line: {
        badge: "Add LINE friend",
        heading: "Add us on LINE to ask questions and share job details",
        description: "Scan the QR code or search for the LINE ID below to add us as a friend, then send your requirements or start with a brief discussion.",
        accountLabel: "LINE ID",
        account: LINE_ID,
        helper: "Search for this LINE ID in the LINE app and tap Add friend to start asking questions or sharing your list.",
        qrAlt: "LINE QR Code",
        qrImage: LINE_QR_IMAGE,
        cta: "Add LINE friend",
        href: LINE_URL
      }
    }
  }
};

const content = computed(() => homeContent[locale.value] || homeContent.th);
const currentSlideIndex = ref(0);
const galleryLightboxIndex = ref(null);
let slideTimer = null;

const openGalleryLightbox = (index) => {
  galleryLightboxIndex.value = index;
};

const closeGalleryLightbox = () => {
  galleryLightboxIndex.value = null;
};

const currentSlide = computed(() => content.value.hero.slides[currentSlideIndex.value] || content.value.hero.slides[0]);

const nextSlide = () => {
  currentSlideIndex.value = (currentSlideIndex.value + 1) % content.value.hero.slides.length;
};

const prevSlide = () => {
  currentSlideIndex.value = (currentSlideIndex.value - 1 + content.value.hero.slides.length) % content.value.hero.slides.length;
};

const syncTitle = () => {
  document.title =
    locale.value === "th" ? "ฐิติมา พาณิชย์ | อุปกรณ์ดับเพลิง เซฟตี้ และวัสดุสำนักงาน" : "Thitima Panich | Fire Safety and Office Supply";
};

onMounted(() => {
  syncTitle();
  slideTimer = window.setInterval(nextSlide, 5000);
});

watch(locale, () => {
  currentSlideIndex.value = 0;
  galleryLightboxIndex.value = null;
  syncTitle();
});

onBeforeUnmount(() => {
  if (slideTimer) window.clearInterval(slideTimer);
});
</script>
